import React, { useRef, useEffect, useState } from 'react';
import Card from './Card';
import _ from 'lodash';
import { RE_RED, RE_BLUE, RE_YELLOW, RE_NAVY, RE_PURPLE, RE_GREEN, MOVE_DURATION, MAX_SECS } from '../const';
import { requestResetStats, requestSurrender, setRestart, forceUpdate } from '../../HOME/REDUX/actions';
import { setInit, updateTime, pause, setOver, updateStats, reveal } from '../REDUX/actions';
import { connect } from 'react-redux';
import { delay, playSound, dbget, dbset } from '../../HOME/utils';
import { GAME_PAGE, SLOW, HELP_PAGE, FAST } from '../../HOME/const';
import * as f from '../logic';
import MessageBox from '../../HOME/COMPONENTS/MessageBox';
import ResumeIcon from '../../HOME/SVG/PlayIcon';
import { useInterval } from '../../HOME/utils';

const Board = props => {
  const { size, rows, cols, stackSize, forceUpdate, surrender, resetStats, paused, over, restart, revealing } = props;
  const isGamePage = props.page === GAME_PAGE;
  const boardId = props.page === HELP_PAGE ? stackSize - 1 : 3;
  const cardCount = rows * cols;
  const l = useRef(null);
  const allAreas = ['1/1', '1/2', '2/1', '2/2', '3/1', '3/2'];
  const allColors = [RE_RED, RE_GREEN, RE_BLUE, RE_YELLOW, RE_NAVY, RE_PURPLE];
  const [stackIndex, setStackIndex] = useState(-1);

  useEffect(() => {
    if (!l.current) {
      load();
    } else if (restart) {
      doRestart();
      props.setRestart(false);
    }

    return () => {
      props.requestResetStats(false);
      props.requestSurrender(false);

      if (isGamePage && !over) {
        const msecs = props.msecs || 0;

        if (msecs % 1000 === 0 && msecs <= MAX_SECS[size - 1] * 1000) {
          save(msecs);
        }
      }
    };
  }, [restart]);

  useEffect(() => {
    return () => {
      if (l.current.revealing) {
        setReveal(false);
      }
    };
  }, []);

  const load = () => {
    l.current = {};

    if (!isGamePage) {
      init(true);
      return;
    }

    dbget(f.boardKey(props), json => {
      if (!json) {
        init(true);
        return;
      }

      props.pause(true);
      const { msecs, cards } = fromJson(json);

      l.current.cards = cards;
      props.updateTime(msecs || 1000);
    });
  };

  const save = msecs => {
    dbset(f.boardKey(props), toJson(msecs));
  };

  const toJson = msecs => {
    const json = JSON.stringify({ msecs, cards: l.current.cards }, ['msecs', 'cards', 'peg', 'hole', 'area', 'color', 'solution']);

    return json;
  };

  const fromJson = json => {
    return JSON.parse(json);
  };

  useInterval(
    () => {
      const msecs = props.msecs || 0;
      props.updateTime(msecs + 100);

      if (msecs % 1000 === 0) {
        save(msecs);
      }
    },
    paused === undefined || paused ? null : 100
  );

  const init = invalidate => {
    if (isGamePage) {
      playSound(props, 'dice');
    }

    l.current = { ...l.current, cards: [] };

    const colors = _.sampleSize(allColors, stackSize);
    let areas = _.sampleSize(allAreas, stackSize);

    for (let i = 0; i < stackSize; i++) {
      const j = i < stackSize - 1 ? i + 1 : 0;

      l.current.cards.push({
        selected: false,
        solution: true,
        peg: { area: areas[i], color: colors[i] },
        hole: { area: areas[j], color: colors[j] },
      });
    }

    for (let i = 0; i < cardCount - stackSize; i++) {
      while (true) {
        const twoAreas = _.sampleSize(allAreas, 2);
        const a1 = twoAreas[0];
        const a2 = twoAreas[1];

        const twoColors = _.sampleSize(allColors, 2);
        const c1 = twoColors[0];
        const c2 = twoColors[1];

        if (!_.find(l.current.cards, c => c.peg.area === a1 && c.peg.color === c1 && c.hole.area === a2 && c.hole.color === c2)) {
          l.current.cards.push({ selected: false, peg: { area: a1, color: c1 }, hole: { area: a2, color: c2 } });
          break;
        }
      }
    }

    l.current.cards = _.shuffle(l.current.cards);

    if (isGamePage) {
      props.updateTime(0);
      props.setOver(false);
      delay(() => props.pause(false), SLOW);
    }

    if (invalidate) {
      Promise.resolve().then(forceUpdate);
    }
  };

  const handleTouch = i => {
    l.current.touch = true;
    handleClick(i);
  };

  const handleMouseDown = i => {
    if (!l.current.touch) {
      handleClick(i);
    }
  };

  const handleClick = i => {
    if (l.current.blocked || revealing || surrender || resetStats) {
      return;
    }

    playSound(props, 'click');

    const card = l.current.cards[i];

    if (stackIndex >= 0) {
      doRestart();
      return;
    }

    card.selected = !card.selected;
    forceUpdate();

    if (!checkGameOver()) {
      return;
    }

    if (props.callback) {
      props.callback(true);
    }

    if (isGamePage) {
      props.pause(true);
      props.setOver(true);

      f.setStats(props);
    }

    l.current.blocked = true;

    makeStack();
  };

  const makeStack = auto => {
    const cards = _.filter(l.current.cards, c => c.selected);

    delay(
      () => {
        const card = _.minBy(cards, c => Math.abs(c.row - 2) + Math.abs(c.col - 2.5));
        const index = _.indexOf(l.current.cards, card);
        setStackIndex(index);

        delay(() => {
          playSound(props, 'cluck');
          delay(() => playSound(props, 'won'), MOVE_DURATION);

          l.current.blocked = false;

          _.each(cards, (c, i) => {
            c.yens = [{ area: `${c.peg.area}`, color: `${c.peg.color}` }];
            c.peg = null;
          });

          forceUpdate();
          setReveal(false);
        }, MOVE_DURATION);
      },
      auto ? MOVE_DURATION : FAST
    );
  };

  const setReveal = on => {
    props.reveal(on);
    l.current.revealing = on;
  };

  const doRestart = () => {
    setStackIndex(-1);

    if (props.callback) {
      props.callback(false);
    }

    l.current.blocked = true;
    props.setInit(boardId);

    delay(() => {
      init();
      props.setInit(0);

      delay(() => {
        l.current.blocked = false;
      }, MOVE_DURATION / 2);
    }, MOVE_DURATION / 2);
  };

  const checkGameOver = () => {
    const cards = _.filter(l.current.cards, c => c.selected);

    if (cards.length !== stackSize) {
      return false;
    }

    const pegs = _.map(cards, 'peg');
    const holes = _.map(cards, 'hole');

    let a1 = _.sortBy(pegs, 'area');
    let a2 = _.sortBy(holes, 'area');

    if (!_.isEqual(a1, a2)) {
      return false;
    }

    a1 = _.sortBy(pegs, 'color');
    a2 = _.sortBy(holes, 'color');

    return _.isEqual(a1, a2);
  };

  if (!l.current) {
    return null;
  }

  const handleConfirmSurrender = yes => {
    delay(() => {
      props.requestSurrender(false);

      if (!yes) {
        return;
      }

      setReveal(true);
      props.pause(true);

      _.each(l.current.cards, c => {
        c.selected = false;
      });

      props.updateTime(-1);
      props.pause(true);
      props.setOver(true);

      f.setStats(props, true);

      for (let i = 0, j = 1; i < l.current.cards.length; i++) {
        const card = l.current.cards[i];

        if (card.solution) {
          delay(() => {
            if (l.current.revealing) {
              playSound(props, 'click');
              card.selected = true;
              forceUpdate();

              if (j === size + 2) {
                makeStack(true);
              }
            }
          }, MOVE_DURATION * j++);
        }
      }
    });
  };

  const handleConfirmResetStats = yes => {
    if (yes) {
      props.updateStats();
    }

    delay(() => props.requestResetStats(false));
  };

  const showResume = isGamePage && paused && !revealing && !over && !restart && !l.current.blocked && props.msecs > 0;

  return (
    <div style={{ display: 'grid' }}>
      <div style={{ gridArea: '1/1', display: 'grid', grid: `${_.repeat('auto ', rows)}/${_.repeat('auto ', cols)}` }}>
        {!showResume
          ? _.map(l.current.cards, (card, i) => {
              card.row = Math.floor(i / cols) + 1;
              card.col = (i % cols) + 1;

              return (
                <div
                  key={i}
                  style={{ gridArea: `${card.row}/${card.col}` }}
                  onTouchStart={() => handleTouch(i)}
                  onMouseDown={() => handleMouseDown(i)}>
                  <Card
                    card={card}
                    boardId={boardId}
                    stack={stackIndex < 0 ? null : { row: Math.floor(stackIndex / cols) + 1, col: (stackIndex % cols) + 1 }}
                  />
                </div>
              );
            })
          : null}
      </div>
      {showResume && !resetStats && !surrender ? (
        <div className="ms-resume fadein-fast" style={{ gridArea: '1/1' }} onClick={() => props.pause(false)}>
          <>
            <span className="re-game-paused">GAME PAUSED</span>
            <ResumeIcon width="100px" color="darkgreen" style={{ gridArea: '1/1' }} />
          </>
        </div>
      ) : null}
      {isGamePage && (
        <div style={{ gridArea: '1/1', alignSelf: 'center', justifySelf: 'center' }}>
          {surrender && <MessageBox message="Give up?" yesNo onReply={handleConfirmSurrender} />}
          {resetStats && <MessageBox message="Reset stats?" yesNo onReply={handleConfirmResetStats} />}
        </div>
      )}
    </div>
  );
};

function mapStateToProps({ re, common }) {
  return { ...re, ...common };
}

export default connect(
  mapStateToProps,
  { requestResetStats, requestSurrender, forceUpdate, setInit, updateTime, pause, setOver, updateStats, setRestart, reveal }
)(Board);
